/**
 * feature-lock.tsx — Frame.io-style soft lock for plan-gated controls.
 *
 * The gated control stays VISIBLE but is dimmed + non-interactive, with a small
 * sparkle badge in the corner. Hovering shows a tooltip naming the plan that
 * unlocks it; clicking anywhere on the control routes to the Plan page to
 * upgrade. When the current plan already grants the feature, children render
 * untouched (zero overhead).
 *
 * The server still 403s below the tier — this is purely the UX layer so users
 * never fill in a control only to hit an error toast.
 */
import type { ReactNode } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Sparkles } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useHasFeature } from "@/hooks/use-plan";
import { FEATURE_META, type FeatureKey } from "@/lib/plan-features";
import { cn } from "@/lib/utils";

export function FeatureLock({
  feature,
  children,
  className,
  side = "top",
  badge = true,
  locked: lockedProp,
}: {
  feature: FeatureKey;
  children: ReactNode;
  className?: string;
  side?: "top" | "right" | "bottom" | "left";
  /** Show the corner sparkle badge (default true). */
  badge?: boolean;
  /** Force locked/unlocked; defaults to the current plan's gate. */
  locked?: boolean;
}) {
  const has = useHasFeature(feature);
  const locked = lockedProp ?? !has;
  const navigate = useNavigate();
  const { pathname } = useLocation();

  if (!locked) return <>{children}</>;

  const minTier = FEATURE_META[feature]?.minTier ?? "a paid";
  const planHref = pathname.startsWith("/admin")
    ? "/admin/settings/plan"
    : "/app/settings/plan";
  const goUpgrade = () => navigate(planHref);

  return (
    <TooltipProvider delayDuration={80}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            role="button"
            tabIndex={0}
            aria-label={`Upgrade to ${minTier} to use this feature`}
            onClick={goUpgrade}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                goUpgrade();
              }
            }}
            className={cn("relative cursor-pointer rounded-lg", className)}
          >
            <div className="pointer-events-none select-none opacity-55">
              {children}
            </div>
            {badge && (
              <span className="absolute right-1.5 top-1.5 z-10 flex h-4 w-4 items-center justify-center rounded-full bg-primary/15 text-primary ring-1 ring-primary/25">
                <Sparkles className="h-2.5 w-2.5" strokeWidth={2} />
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent side={side} className="max-w-[230px] text-left font-normal">
          <span className="flex items-start gap-1.5">
            <Sparkles className="mt-0.5 h-3 w-3 shrink-0 text-primary" strokeWidth={2} />
            <span>
              This feature is available on the{" "}
              <span className="font-semibold text-foreground">{minTier}</span> plan
              and above. Click to upgrade.
            </span>
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

/**
 * FeatureUpgradeBanner — a full-width soft-lock banner for a whole gated
 * settings section (branding, authorized domains, …). Renders nothing when the
 * plan already grants the feature, so callers can drop it in unconditionally.
 */
export function FeatureUpgradeBanner({
  feature,
  title,
  className,
}: {
  feature: FeatureKey;
  title: string;
  className?: string;
}) {
  const has = useHasFeature(feature);
  const { pathname } = useLocation();
  if (has) return null;
  const minTier = FEATURE_META[feature]?.minTier ?? "a paid";
  const planHref = pathname.startsWith("/admin")
    ? "/admin/settings/plan"
    : "/app/settings/plan";
  return (
    <div
      className={cn(
        "mb-6 flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 px-4 py-3",
        className
      )}
    >
      <Sparkles className="h-4 w-4 shrink-0 text-primary" strokeWidth={2} />
      <div className="flex-1 text-sm">
        <span className="font-medium text-foreground">{title}</span>{" "}
        <span className="text-muted-foreground">
          is available on the {minTier} plan and above.
        </span>
      </div>
      <Link
        to={planHref}
        className="shrink-0 rounded-md bg-primary px-3 py-1.5 text-xs font-medium text-primary-foreground hover:opacity-90"
      >
        View plans
      </Link>
    </div>
  );
}
